'use client'

import { type InputHTMLAttributes, type ReactNode, forwardRef } from 'react'

import { FieldCaption } from '@src/components/FieldCaption/FieldCaption'
import { Spinner } from '@src/components/Spinner/Spinner'
import { getFieldCaptionTestId } from '@src/utils/common.util'
import { cn } from '@src/utils/tailwind'

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  id: string
  labelText: string
  isInvalid?: boolean
  isSuccess?: boolean
  isLoading?: boolean
  loadingComponent?: ReactNode
  captionText?: string
  iconRight?: ReactNode
  noBorder?: boolean
  children?: ReactNode
  containerClassName?: string
  ['data-testid']?: string
}

export const TextField = forwardRef<HTMLInputElement, Props>((props, ref) => {
  const {
    id,
    labelText,
    isInvalid,
    isSuccess,
    isLoading,
    loadingComponent,
    captionText,
    className,
    containerClassName,
    iconRight,
    noBorder,
    children,
    placeholder,
    ...rest
  } = props

  const hasPlaceholder = Boolean(placeholder && placeholder.trim().length > 0)

  return (
    <div className={cn('w-full', containerClassName)}>
      <div className={cn('relative w-full rounded-md')}>
        <div className="group relative w-full">
          <input
            type="text"
            id={id}
            ref={ref}
            placeholder={placeholder ?? ' '}
            {...rest}
            className={cn(
              `
            peer block h-14 w-full rounded-md border-0 bg-bodi-white px-4 pt-6 text-body-md leading-6 text-bodi-black ring-1 ring-bodi-gray-butters
            outline-none

            hover:ring-bodi-gray-medium

            focus:bg-bodi-white
            focus:ring-2
            focus:ring-bodi-black-text
            `,
              {
                'ring-bodi-red-deep-500 hover:ring-bodi-red-deep-600 focus:ring-2 focus:ring-bodi-red-deep-500':
                  isInvalid,
                'ring-bodi-green-success hover:ring-bodi-green-success focus:ring-2 focus:ring-bodi-green-success':
                  isSuccess,
                'pr-11': iconRight,
              },
              { 'ring-0 focus:ring-0': noBorder },
              {
                'disabled:bg-bodi-gray-pearl disabled:text-bodi-gray-disabled disabled:ring-bodi-gray-disabled':
                  !isLoading,
              },
              className
            )}
          />
          <label
            htmlFor={id}
            className={cn(
              `pointer-events-none absolute top-0 left-0 flex h-full items-center pl-4 text-body-md text-bodi-gray-medium transition-all`,
              'peer-focus:h-1/2 peer-focus:translate-y-1 peer-focus:text-body-xs',
              'peer-not-placeholder-shown:h-1/2 peer-not-placeholder-shown:translate-y-1 peer-not-placeholder-shown:text-body-xs',
              {
                'h-1/2 translate-y-1 text-body-xs': hasPlaceholder,
                'text-bodi-gray-disabled': rest.disabled,
              }
            )}
          >
            {labelText}
          </label>
          <div
            className={cn('absolute top-1/2 right-4 -translate-y-1/2 *:size-5', {
              'text-bodi-gray-disabled': rest.disabled,
            })}
          >
            {iconRight}
          </div>
          {isLoading && (
            <div className="absolute top-1/2 right-4 -translate-y-1/2">
              {loadingComponent ?? <Spinner size="smaller" />}
            </div>
          )}
          {children}
        </div>
      </div>
      {captionText && (
        <div className="w-full">
          <FieldCaption
            captionText={captionText}
            isInvalid={isInvalid}
            isSuccess={isSuccess}
            disabled={rest.disabled}
            data-testid={getFieldCaptionTestId(rest['data-testid'], isInvalid)}
          />
        </div>
      )}
    </div>
  )
})

TextField.displayName = 'TextField'
